import axios from "axios";
import { getDraftPayloadFromResponse } from "./draftService";
import {
    attachPollErrorDiagnostics,
    getAxiosCode,
    getAxiosHttpStatus,
    getPollElapsedMs,
    getPollStatusRequestDescriptor,
    runPreviewStatusPolling,
} from "./previewPollingResilience";
import { runPreviewPollStartOnce } from "./previewPollStartGuard";
import {
    createPreviewCancellation,
    extractPreviewPollJobId,
    extractPreviewPollResult,
    getPreviewHttpJsonConfig,
    getPreviewPollStatus,
    hashPreviewString,
    isPreviewPollCompleted,
    isPreviewPollFailed,
    isPreviewPollRunning,
    normalizePreviewPollInterval,
    stablePreviewStringify,
} from "./previewService";

export const CHANGE_REQUEST_POLL_CANCELLED = "CHANGE_REQUEST_POLL_CANCELLED";

// The status endpoint returns the ChangePreview_NEW response as result; like
// the preview it may also arrive unwrapped, then it carries the story pages.
const extractChangeResult = (payload) => extractPreviewPollResult(
    payload,
    (candidate) => Boolean(candidate.pages)
);

const createChangePollError = (message, failureReason, diagnostics = {}) => (
    attachPollErrorDiagnostics(new Error(message), { ...diagnostics, failureReason })
);

/**
 * Runs a change request as a backend job: start it once, then poll its status
 * until the changed story is ready. Resolves with the same response data the
 * synchronous change request returns, so callers can treat both modes alike.
 */
export async function runChangeRequestPolling({
    requestBody,
    pollStartUrl,
    pollStatusUrl,
    pollIntervalMs = 3000,
    pollTimeoutMs = 900000,
    isActive = () => true,
    http = axios,
}) {
    const cancellation = createPreviewCancellation({
        cancelCode: CHANGE_REQUEST_POLL_CANCELLED,
        isActive,
    });
    // sentry-ignore: start and status are retried; ChangeRequest reports the final outcome once
    const httpConfig = { ...getPreviewHttpJsonConfig(30000), sentryReport: false };
    const createdAt = Date.now();
    let pollJob = { jobId: null, createdAt };
    let result = null;

    const buildDiagnostics = ({
        error = null,
        pollJob: job = pollJob,
        descriptor = null,
        response = null,
        pollPhase = "status",
        failureReason = "request_failed",
        finalCheck = false,
    } = {}) => ({
        component: "ChangeRequest",
        jobId: job?.jobId || null,
        pollPhase,
        requestUrl: descriptor?.url || (pollPhase === "start" ? pollStartUrl : pollStatusUrl),
        httpStatus: response?.status || getAxiosHttpStatus(error),
        axiosCode: getAxiosCode(error),
        elapsedMs: getPollElapsedMs(job),
        failureReason,
        finalCheck,
    });

    // Returns done once the result is in, otherwise the next poll step.
    const evaluatePayload = (payload, fallbackIntervalMs, requireKnownStatus, diagnostics) => {
        if (isPreviewPollCompleted(payload)) {
            result = extractChangeResult(payload);
            if (!result) {
                throw createChangePollError(
                    "Polling wurde abgeschlossen, aber es fehlt result.",
                    "missing_result",
                    diagnostics
                );
            }
            return { done: true };
        }

        if (isPreviewPollFailed(payload)) {
            throw createChangePollError(
                payload?.message || payload?.error || "Change request failed",
                "backend_failed",
                diagnostics
            );
        }

        const status = getPreviewPollStatus(payload);
        if (requireKnownStatus && (!status || !isPreviewPollRunning(payload))) {
            throw createChangePollError(
                `Unbekannter Polling-Status: ${status || "leer"}`,
                "unknown_status",
                diagnostics
            );
        }

        const intervalMs = normalizePreviewPollInterval(payload?.pollIntervalMs, fallbackIntervalMs);
        pollJob = {
            ...pollJob,
            jobId: extractPreviewPollJobId(payload) || pollJob.jobId,
            status: status || "processing",
            pollIntervalMs: intervalMs,
            expiresAt: payload?.expiresAt || pollJob.expiresAt || null,
        };
        return { done: false, pollJob, intervalMs };
    };

    const startDescriptor = { method: "post", url: pollStartUrl, body: null };
    let startResponse;
    try {
        startResponse = await runPreviewPollStartOnce(
            `change-request:${hashPreviewString(stablePreviewStringify(requestBody))}`,
            ({ idempotencyKey }) => http.post(pollStartUrl, { ...requestBody, idempotencyKey }, httpConfig),
            { isActive }
        );
    } catch (error) {
        throw attachPollErrorDiagnostics(error, buildDiagnostics({
            error,
            descriptor: startDescriptor,
            pollPhase: "start",
        }));
    }

    const startPayload = getDraftPayloadFromResponse(startResponse.data);
    pollJob = { ...pollJob, jobId: extractPreviewPollJobId(startPayload) };
    const startEvaluation = evaluatePayload(
        startPayload,
        pollIntervalMs,
        false,
        buildDiagnostics({ descriptor: startDescriptor, response: startResponse, pollPhase: "start" })
    );
    if (startEvaluation.done) return result;

    if (!pollJob.jobId) {
        throw createChangePollError(
            "Polling konnte nicht gestartet werden: jobId fehlt.",
            "missing_job_id",
            buildDiagnostics({ descriptor: startDescriptor, response: startResponse, pollPhase: "start" })
        );
    }

    await runPreviewStatusPolling({
        initialPollJob: pollJob,
        requestBody,
        initialIntervalMs: startEvaluation.intervalMs,
        pollTimeoutMs,
        waitForPollInterval: cancellation.wait,
        assertPollActive: cancellation.assertActive,
        isPollJobExpired: (job) => {
            const expiresAtMs = Date.parse(job?.expiresAt || "");
            if (Number.isFinite(expiresAtMs) && expiresAtMs <= Date.now()) return true;
            return pollTimeoutMs > 0 && Date.now() - createdAt >= pollTimeoutMs;
        },
        // Change requests are not resumed after a reload, so the job only lives here.
        clearStoredPollJob: () => {},
        persistPollJob: (_storageKey, _requestBody, job) => {
            pollJob = { ...pollJob, ...job };
            return pollJob;
        },
        normalizePollIntervalMs: normalizePreviewPollInterval,
        getStatusRequestDescriptor: (jobId) => getPollStatusRequestDescriptor(pollStatusUrl, jobId),
        requestStatus: (descriptor) => (descriptor.method === "get"
            ? http.get(descriptor.url, httpConfig)
            : http.post(descriptor.url, descriptor.body, httpConfig)),
        getStatusPayload: (response) => getDraftPayloadFromResponse(response.data),
        evaluateStatusPayload: (payload, _storageKey, _requestBody, fallbackIntervalMs, requireKnownStatus, diagnostics) => (
            evaluatePayload(payload, fallbackIntervalMs, requireKnownStatus, diagnostics)
        ),
        buildDiagnostics,
        isCancelledError: cancellation.isCancelled,
    });

    return result;
}
